import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { fetchMiniStoryboardStatistics, fetchMiniStoryboardSegmentStatistics } from '../../../services/adminService';
import { miniStoryboardSegmentPath } from '../../../utils/miniStoryboardNavigation';
import { displayUserId } from '../adminUtils';
import { EmptyBlock, ErrorNotice, Panel, SecondaryButton } from '../adminUi';

const secondary = { color: 'var(--color-text-secondary)' };
const muted = { color: 'var(--color-text-muted)' };
const border = { borderColor: 'var(--color-border-primary)' };
const number = value => Number(value || 0).toLocaleString('zh-CN');
const time = value => value == null ? '—' : new Date(value).toLocaleString('zh-CN', { hour12: false });

// 有效生成 = 成功 + 进行中 + 其他状态（失败/取消不扣钱，不计入）
const validGenerations = counts => Number(counts?.successCount || 0) + Number(counts?.activeCount || 0) + Number(counts?.otherCount || 0);

// 重试百分比 = (有效生成次数 − 片段数) ÷ 片段数；片段为 0 或未生成时不显示
const retryRate = (segmentCount, counts) => {
  const segments = Number(segmentCount || 0);
  if (!segments) return null;
  const valid = validGenerations(counts);
  if (valid <= segments) return 0;
  return (valid - segments) / segments;
};

function RetryRateBadge({ segmentCount, counts }) {
  const rate = retryRate(segmentCount, counts);
  if (rate == null) return <span className="text-xs" style={muted}>—</span>;
  const percent = Math.round(rate * 100);
  const color = percent >= 100 ? 'var(--color-error)' : percent >= 50 ? 'var(--color-warning)' : 'var(--color-success)';
  return <span className="font-mono-num text-sm font-semibold" style={{ color }}
    title={`有效生成 ${number(validGenerations(counts))} 次 ÷ 片段 ${number(segmentCount)} 个（失败/取消不计入）`}>
    {percent}%
  </span>;
}

function OutcomeCounts({ counts }) {
  const entries = [
    ['successCount', '成功', 'var(--color-success)'], ['failedCount', '失败', 'var(--color-error)'],
    ['activeCount', '进行中', 'var(--color-info)'], ['canceledCount', '取消', 'var(--color-text-muted)'],
    ['otherCount', '其他状态', 'var(--color-warning)'],
  ];
  return <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
    {entries.filter(([key]) => Number(counts?.[key]) > 0).map(([key, label, color]) => (
      <span key={key} style={color.startsWith('var') ? { color } : undefined}>{label} {number(counts[key])}</span>
    ))}
    {!counts?.generationCount && <span style={muted}>尚未生成</span>}
  </div>;
}

function EpisodeSegments({ projectId, episodeId }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    fetchMiniStoryboardSegmentStatistics(projectId, episodeId, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setResult(data); })
      .catch(e => { if (!controller.signal.aborted) setError(e.message || '片段统计加载失败'); });
    return () => controller.abort();
  }, [projectId, episodeId, retry]);

  if (error) return <div className="p-4"><ErrorNotice message={error} />
    <SecondaryButton onClick={() => { setError(''); setRetry(value => value + 1); }}>重新加载片段</SecondaryButton>
  </div>;
  if (!result) return <p role="status" className="p-5 text-sm" style={muted}>正在读取片段与生成人员…</p>;
  if (!result.segments?.length) return <div className="p-4"><EmptyBlock text="这一集还没有小故事板片段" hint="拆分或添加片段后即可在这里查看统计。" /></div>;

  return <div className="overflow-x-auto">
    <table className="w-full text-sm text-left" style={{ minWidth: 780 }}>
      <caption className="sr-only">每个片段的生成次数、生成人员和最近生成时间</caption>
      <thead className="text-xs" style={{ ...muted, backgroundColor: 'var(--color-bg-primary)' }}>
        <tr>{['片段 / 跳转', '生成任务次数', '生成人员 · 次数', '最近生成'].map(label => <th scope="col" key={label} className="px-4 py-3 font-medium">{label}</th>)}</tr>
      </thead>
      <tbody>{result.segments.map(segment => <tr key={segment.segmentId} className="border-t align-top" style={border}>
        <th scope="row" className="px-4 py-4 font-normal" style={{ width: '30%' }}>
          <a href={miniStoryboardSegmentPath(projectId, episodeId, segment.segmentId)} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-start gap-2 font-medium hover:underline rounded focus-visible:outline focus-visible:outline-2"
            style={{ color: 'var(--color-warning)' }} aria-label={`打开片段 ${segment.segmentIndex}：${segment.title || '未命名'}（新标签页）`}>
            <span className="break-words">片段 {String(segment.segmentIndex).padStart(2, '0')} · {segment.title || '未命名片段'}</span>
            <ExternalLink size={13} className="shrink-0 mt-1" aria-hidden="true" />
          </a>
          <div className="text-xs mt-1.5" style={muted}>ID {segment.segmentId}{segment.durationSeconds != null ? ` · ${segment.durationSeconds} 秒` : ''}</div>
        </th>
        <td className="px-4 py-4">
          <div className="font-mono-num text-lg font-semibold mb-1">{number(segment.counts.generationCount)} <span className="text-xs font-normal" style={muted}>次</span></div>
          <OutcomeCounts counts={segment.counts} />
        </td>
        <td className="px-4 py-4" style={{ width: '30%' }}>
          <div className="flex flex-wrap gap-2">{segment.actors.map(actor => <span key={actor.userId}
            className="inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs max-w-full" style={border}>
            <span className="break-all" style={secondary}>{displayUserId(actor.userId, '未记录人员')}</span>
            <span className="font-mono-num font-semibold shrink-0">{number(actor.counts.generationCount)} 次</span>
          </span>)}{!segment.actors.length && <span className="text-xs" style={muted}>—</span>}</div>
        </td>
        <td className="px-4 py-4 text-xs whitespace-nowrap" style={secondary}>{time(segment.counts.lastGeneratedAt)}</td>
      </tr>)}</tbody>
    </table>
  </div>;
}

// ============================
// 集数行（项目内）：片段数 + 生成次数 + 成败 + 重试百分比同排展示，点击展开片段
// ============================

function EpisodeStatsRow({ episode }) {
  const [open, setOpen] = useState(false);
  const valid = validGenerations(episode.counts);
  return <>
    <tr onClick={() => setOpen(value => !value)} aria-expanded={open}
      className="hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer" style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
      <td className="py-2.5 pr-4">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {open ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
          {episode.episodeTitle || `第 ${episode.sortOrder || episode.episodeId} 集`}
        </span>
      </td>
      <td className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap">{number(episode.segmentCount)}</td>
      <td className="py-2.5 pr-4 text-xs font-mono-num font-semibold text-right whitespace-nowrap" style={{ color: 'var(--color-warning)' }}>{number(valid)}</td>
      <td className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap" style={{ color: 'var(--color-success)' }}>{number(episode.counts?.successCount)}</td>
      <td className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap" style={muted}>{number(episode.counts?.failedCount)}</td>
      <td className="py-2.5 pr-4 text-right whitespace-nowrap"><RetryRateBadge segmentCount={episode.segmentCount} counts={episode.counts} /></td>
      <td className="py-2.5 text-xs text-right whitespace-nowrap" style={secondary}>{time(episode.counts?.lastGeneratedAt)}</td>
    </tr>
    {open && (
      <tr>
        <td colSpan={7} className="p-0" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
          <div className="animate-in fade-in duration-150">
            <EpisodeSegments projectId={episode.projectId} episodeId={episode.episodeId} />
          </div>
        </td>
      </tr>
    )}
  </>;
}

// ============================
// 视图 2：项目内各集
// ============================

function ProjectEpisodeView({ project, keyword, onBack }) {
  const episodes = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = project.episodes.slice().sort((a, b) => Number(a.sortOrder || a.episodeId) - Number(b.sortOrder || b.episodeId));
    if (!q) return list;
    return list.filter(episode =>
      String(episode.episodeTitle || '').toLowerCase().includes(q)
      || String(episode.episodeId).includes(q));
  }, [project, keyword]);

  return <Panel
    title={project.name || '未命名项目'}
    sub={`项目 #${project.projectId} · ${number(project.episodes.length)} 集 · 点击集数行展开片段明细`}
    right={<SecondaryButton className="inline-flex items-center gap-1.5" onClick={onBack}><ChevronLeft size={14} />返回项目列表</SecondaryButton>}
  >
    {!episodes.length ? (
      <EmptyBlock text="没有符合条件的集数" hint="可清空搜索后重试。" />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left" style={{ minWidth: 860 }}>
          <caption className="sr-only">该项目每一集的片段数、生成次数、成败与重试百分比</caption>
          <thead className="text-xs" style={muted}>
            <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
              {['集数', '片段数', '生成次数', '成功', '失败', '重试百分比', '最近生成'].map(label => (
                <th scope="col" key={label} className={`pb-2.5 pr-4 font-medium ${label === '集数' ? '' : 'text-right'}`}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {episodes.map(episode => <EpisodeStatsRow key={episode.episodeId} episode={episode} />)}
          </tbody>
        </table>
      </div>
    )}
    <p className="text-xs leading-5 mt-4" style={muted}>
      口径：生成次数 = 成功 + 进行中 + 其他（失败/取消不扣钱，不计入）；重试百分比 =（生成次数 − 片段数）÷ 片段数，越高说明该集反复抽卡越多。
    </p>
  </Panel>;
}

// ============================
// 视图 1：项目总览（先进项目，再看集数）
// ============================

function ProjectOverview({ items, keyword, onSelectProject }) {
  const projects = useMemo(() => {
    const groups = new Map();
    items.forEach(episode => {
      if (!groups.has(episode.projectId)) {
        groups.set(episode.projectId, { projectId: episode.projectId, name: episode.projectName, episodes: [] });
      }
      groups.get(episode.projectId).episodes.push(episode);
    });
    const q = keyword.trim().toLowerCase();
    let list = [...groups.values()].map(group => {
      const totals = group.episodes.reduce((acc, episode) => ({
        segments: acc.segments + Number(episode.segmentCount || 0),
        generationCount: acc.generationCount + Number(episode.counts?.generationCount || 0),
        successCount: acc.successCount + Number(episode.counts?.successCount || 0),
        failedCount: acc.failedCount + Number(episode.counts?.failedCount || 0),
        activeCount: acc.activeCount + Number(episode.counts?.activeCount || 0),
        otherCount: acc.otherCount + Number(episode.counts?.otherCount || 0),
        canceledCount: acc.canceledCount + Number(episode.counts?.canceledCount || 0),
        lastGeneratedAt: Math.max(acc.lastGeneratedAt || 0, Number(episode.counts?.lastGeneratedAt || 0)),
      }), { segments: 0, generationCount: 0, successCount: 0, failedCount: 0, activeCount: 0, otherCount: 0, canceledCount: 0, lastGeneratedAt: null });
      return { ...group, totals };
    });
    if (q) {
      list = list.filter(project =>
        String(project.name || '').toLowerCase().includes(q)
        || String(project.projectId).includes(q)
        || project.episodes.some(episode => String(episode.episodeTitle || '').toLowerCase().includes(q)));
    }
    list.sort((a, b) => validGenerations(b.totals) - validGenerations(a.totals));
    return list;
  }, [items, keyword]);

  if (!projects.length) return <EmptyBlock text="暂无符合条件的项目" hint="可清空搜索后重试。" />;

  return <>
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs mb-3" style={muted}>
      <span>共 {number(projects.length)} 个项目 · 按生成次数排序 · 点击项目查看各集</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left" style={{ minWidth: 860 }}>
        <caption className="sr-only">每个项目的集数、片段数、生成次数、成败与重试百分比</caption>
        <thead className="text-xs" style={muted}>
          <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
            {['项目', '集数', '片段数', '生成次数', '成功', '失败', '重试百分比', '最近生成'].map(label => (
              <th scope="col" key={label} className={`pb-2.5 pr-4 font-medium ${label === '项目' ? '' : 'text-right'}`}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {projects.map(project => (
            <tr key={project.projectId} onClick={() => onSelectProject(project)}
              className="hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer"
              style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
              <td className="py-3 pr-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--color-warning)' }}>
                  {project.name || '未命名项目'}
                  <ChevronRight size={13} aria-hidden="true" />
                </span>
                <span className="block text-2xs mt-0.5" style={muted}>项目 #{project.projectId}</span>
              </td>
              <td className="py-3 pr-4 text-xs font-mono-num text-right whitespace-nowrap">{number(project.episodes.length)}</td>
              <td className="py-3 pr-4 text-xs font-mono-num text-right whitespace-nowrap">{number(project.totals.segments)}</td>
              <td className="py-3 pr-4 text-xs font-mono-num font-semibold text-right whitespace-nowrap" style={{ color: 'var(--color-warning)' }}>{number(validGenerations(project.totals))}</td>
              <td className="py-3 pr-4 text-xs font-mono-num text-right whitespace-nowrap" style={{ color: 'var(--color-success)' }}>{number(project.totals.successCount)}</td>
              <td className="py-3 pr-4 text-xs font-mono-num text-right whitespace-nowrap" style={muted}>{number(project.totals.failedCount)}</td>
              <td className="py-3 pr-4 text-right whitespace-nowrap"><RetryRateBadge segmentCount={project.totals.segments} counts={project.totals} /></td>
              <td className="py-3 text-xs text-right whitespace-nowrap" style={secondary}>{time(project.totals.lastGeneratedAt || null)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>;
}

// ============================
// 页面主体
// ============================

// 后端每页上限 100，超出会被拒（"每页数量须为 1-100"），大项目靠翻页拉全
const PAGE_SIZE = 100;

async function fetchAllEpisodeStats(keyword) {
  const all = [];
  let page = 0;
  let totalPages = 1;
  do {
    const res = await fetchMiniStoryboardStatistics({ keyword, page, size: PAGE_SIZE });
    all.push(...(res?.items || []));
    totalPages = Number(res?.totalPages || 1);
    page += 1;
  } while (page < totalPages);
  return all;
}

function StatisticsView() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchAllEpisodeStats(query)
      .then(data => { if (!controller.signal.aborted) { setItems(data); setLoading(false); } })
      .catch(e => { if (!controller.signal.aborted) { setError(e.message || '小故事板统计加载失败'); setLoading(false); } });
    return () => controller.abort();
  }, [query, revision]);

  const applyQuery = (next) => { setQuery(next); setSelectedProject(null); setError(''); };
  const refresh = () => { setRevision(value => value + 1); setSelectedProject(null); setLoading(true); setError(''); };

  return <div>
    <div className="mb-5">
      <h2 className="text-lg font-semibold">小故事板生成统计</h2>
      <p className="text-xs mt-2 leading-5" style={muted}>先看项目，点进项目看每一集；片段数、生成次数、成败与重试百分比同排展示，哪一集抽卡多一眼可见。点击集数行展开片段明细（新标签页可定位到片段）。</p>
    </div>
    <Panel title={selectedProject ? '项目各集统计' : '项目总览'} sub={selectedProject ? undefined : '全部项目'}>
      <form onSubmit={event => { event.preventDefault(); setLoading(true); applyQuery(search.trim()); }}
        className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex-1 min-w-48 max-w-md text-xs" style={secondary}>
          <span className="block mb-1.5">搜索项目或集数</span>
          <input value={search} onChange={event => setSearch(event.target.value)} maxLength={100}
            placeholder="项目名、集名、项目 ID 或集 ID" className="h-8 w-full px-3 rounded-md border focus-visible:outline focus-visible:outline-2"
            style={{ ...border, backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)' }} />
        </label>
        <button type="submit" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer" style={{ backgroundColor: 'var(--color-warning)', color: 'var(--color-bg-primary)' }}><Search size={14} />查询</button>
        <SecondaryButton className="inline-flex items-center gap-1.5" onClick={refresh}><RefreshCw size={14} className={loading ? "animate-spin" : ""} />刷新统计</SecondaryButton>
        {query && <SecondaryButton onClick={() => { setSearch(''); setLoading(true); applyQuery(''); }}>清空搜索</SecondaryButton>}
      </form>
      {error && <ErrorNotice message={error} />}
      {!items && loading && <p className="py-8 text-center text-sm" style={muted} role="status">正在加载统计…</p>}
      {items && !selectedProject && (
        <ProjectOverview items={items} keyword={query} onSelectProject={setSelectedProject} />
      )}
      {items && selectedProject && (
        <ProjectEpisodeView project={selectedProject} keyword={query} onBack={() => setSelectedProject(null)} />
      )}
      <p className="text-xs leading-5 mt-5" style={muted}>统计口径：当前保留片段的已记录视频任务，每个任务计 1 次；失败/取消不扣钱，不计入生成次数与重试百分比；查询进度不会增加次数，超分任务不计入。未记录人员不推算为项目所有者。</p>
    </Panel>
  </div>;
}

export default function MiniStoryboardStatisticsTab() {
  return <StatisticsView />;
}
